import React from 'react';
import { getDateFromIso8601String } from './DatePicker';
import { calculateAge } from './DatePicker';
import { logEvent } from '../util/apiUtils';

/**
 * Creates a button, styled for use on the PITL Summary page, that will perform a specific action.
 *
 * @param props.title The text to display in the button
 * @param props.onClick The function to call when the button is clicked
 */
export const PITLSummaryActionButton = ({ title, onClick }) => {
	return (
		<button className={`btn btn-md btn-gdx-teal btn-radius-circle`} onClick={onClick} title={title}>
			{title}
		</button>
	);
};

/**
 * Creates a button, styled for use on the Information Review (Confirmation) page, that will
 * perform a specific action.
 *
 * @param props.title The title of the button. Also used as the text to display in the button when
 *                    no children are provided
 * @param props.colorClass An optional className to use to style the button
 * @param props.onClick The function to call when the button is clicked
 * @param props.children An optional array of child elements of the component
 * @param props Any other properties to pass to the button
 */
export const OrderReviewActionButton = ({ title, colorClass, onClick, children, ...props }) => {
	return (
		<button
			className={`btn btn-primary radius btn-sm ${colorClass ?? ''}`}
			title={title}
			style={{ minWidth: '120px', borderRadius: '10px' }}
			onClick={onClick}
			{...props}
		>
			{children || title}
		</button>
	);
};

/**
 * Clone the button element and merge in new properties.
 * The main use of this is to insert the 'onClick' method into an existing element.
 */
const implementActionButton = (button, props) => {
	return React.cloneElement(button, props);
};

/**
 * Creates a button that will open a link to fedex tracking.
 *
 * @param props.trackingInformation An object that contains FedEx tracking numbers for the order
 * @param button (optional) The button element to use. A PITLSummary button will be used if no
 *               button is provided
 */
export const TrackShipmentAction = ({ gearOrderId, trackingInformation, button }) => {
	if (trackingInformation?.trackingNumberList?.length) {
		const trackingNumbers = trackingInformation.trackingNumberList.join(',');
		const fedExUrl = `https://www.fedex.com/fedextrack/?action=track&tracknumbers=${trackingNumbers}`;

		const onClick = () => {
			window.open(fedExUrl, '_blank');

			logEvent(
				'{} - "Track Shipment" clicked. gearOrderId={}, trackingNumbers={}',
				'UI_ACTION_TRACK_SHIPMENT',
				gearOrderId, trackingNumbers
			);
		}

		if (button) {
			return implementActionButton(button, { onClick });
		}
		return <PITLSummaryActionButton title="Track Shipment" onClick={onClick} />;
	}

	return null;
};

/**
 * Creates an ActionButton that will open the e-req.
 * onClick, we will fetch a new JWT token and then open the e-req in a new window.
 *
 * @param props.gearOrder The order for which to open the e-req
 * @param props.userInfo The details of the current user
 */
export const DownloadReceiptAction = ({ gearOrder, userInfo, button }) => {
	const gearOrderId = gearOrder?.id;
	let eReqUrl = `${process.env.REACT_APP_GEAR_REST_URL}/packing-slip/order/${gearOrderId}`;

	// 'prc' users need to include the dateOfBirth in the link for validation
	if (userInfo?.type !== 'myGDX') {
		const patientDOB = gearOrder?.patientInfo?.dateOfBirth;

		// Don't show the e-req button if we don't have the dateOfBirth required for validation
		if (!patientDOB) {
			return null;
		}

		eReqUrl += `?dateOfBirth=${patientDOB}`;
	}

	/**
	 * Opens an electronic requisition PDF.
	 */
	const viewEReq = (event) => {
		event.preventDefault();
		// Call fetch() to generate a new access_token before opening the e-req window
		fetch()
			.then(() => {
				window.open(eReqUrl, '_blank');
			})
			.finally(() => {
				// Add a delay before logging the action to prevent a race condition where the log action nonce overwrites the e-req nonce
				setTimeout(() => {
					logEvent('{} - "View Receipt" clicked. gearOrderId={}', 'UI_ACTION_VIEW_EREQ', gearOrderId);
				}, 100);
			});
	};

	if (button) {
		return implementActionButton(button, { onClick: viewEReq });
	}

	return <PITLSummaryActionButton title="Download Receipt" onClick={viewEReq} />;
};

export const DownloadPatientAgreementAction = ({ gearOrder, userInfo, button }) => {
	const gearOrderId = gearOrder?.id;
	let eReqUrl = `${process.env.REACT_APP_GEAR_REST_URL}/acknowledgement/order/${gearOrderId}/pdf`;

	// 'prc' users need to include the dateOfBirth in the link for validation
	if (userInfo?.type !== 'myGDX') {
		const patientDOB = gearOrder?.patientInfo?.dateOfBirth;

		// Don't show the e-req button if we don't have the dateOfBirth required for validation
		if (!patientDOB) {
			return null;
		}

		eReqUrl += `?dateOfBirth=${patientDOB}`;
	}

	/**
	 * Opens an electronic requisition PDF.
	 */
	const viewPatientAgreement = (event) => {
		event.preventDefault();
		// Call fetch() to generate a new access_token before opening the e-req window
		fetch()
		.then(() => {
			window.open(eReqUrl, '_blank');
		})
		.finally(() => {
			// Add a delay before logging the action to prevent a race condition where the log action nonce overwrites the e-req nonce
			setTimeout(() => {
				logEvent('{} - "View Patient Agreement" clicked. gearOrderId={}', 'UI_ACTION_VIEW_EREQ', gearOrderId);
			}, 100);
		});
	};

	if (button) {
		return implementActionButton(button, { onClick: viewPatientAgreement });
	}

	return <PITLSummaryActionButton title="Download Patient Agreement" onClick={viewPatientAgreement} />;
};

export const InteractiveReportAction = ({ gearOrder, accessionDetail, button }) => {
	// Confirm the accession is for a "Microbiomix" product
	const accessionNo = accessionDetail?.accessionNo;
	const isMicrobiomix = accessionDetail?.productName?.toLowerCase().includes('microbiomix');
	const gearOrderId = gearOrder?.id;
	const patientShippingZipCode = gearOrder?.patientInfo?.addresses?.find(
		(address) => address.type === 'shipping'
	)?.zipCode;

	if (!gearOrderId || !accessionNo || !isMicrobiomix || !patientShippingZipCode) {
		return null;
	}

	let interactiveReportUrl = `${process.env.REACT_APP_GDX_WEB_URL}/prc/reporting/ireport/${accessionNo}?orderId=${gearOrderId}&zipCode=${patientShippingZipCode}`;

	const viewInteractiveReport = (event) => {
		event.preventDefault();

		window.open(interactiveReportUrl, '_blank');

		logEvent(
			'{} - "View Interactive Report" clicked. gearOrderId={}, accessionNo={}, zipCode={}',
			'UI_ACTION_INTERACTIVE_REPORT',
			gearOrderId, accessionNo, patientShippingZipCode
		);
	};

	if (button) {
		return implementActionButton(button, { onClick: viewInteractiveReport });
	}

	return <PITLSummaryActionButton title="View Interactive Report" onClick={viewInteractiveReport} />;
};

/**
 * Creates an ActionButton that will cancel an order.
 */
export const CancelOrderAction = ({ gearOrder, onClick, button }) => {
	// SCR-7558: Only show the cancel button if we're within 45 days of the delivery date
	if (gearOrder?.trackingInformation?.deliveryDate) {
		const deliveryDate = getDateFromIso8601String(gearOrder.trackingInformation.deliveryDate);
		const now = new Date();
		const msSinceDelivery = now - deliveryDate;

		// convert ms to days                      hour   min  sec   ms
		const daysSinceDelivery = msSinceDelivery / (24 * 60 * 60 * 1000);

		if (daysSinceDelivery > 45) {
			return null;
		}
	}

	if (button) {
		return implementActionButton(button, { onClick: onClick });
	}

	return <PITLSummaryActionButton title="Cancel Order" onClick={onClick} />;
};

/**
 * Creates an ActionButton that will open a link to Cynergy's consultation scheduling
 */
export const ScheduleConsultAction = ({ gearOrderId, button }) => {
	const onClick = () => {
		window.open('https://secure.highlandwebforms.com/forms/18146/22820/WlTJ/form.html', '_blank');

		logEvent(
			'{} - "Schedule a Consult" clicked. gearOrderId={}',
			'UI_ACTION_SCHEDULE_CONSULT',
			gearOrderId
		);
	};

	if (button) {
		return implementActionButton(button, { onClick: onClick });
	}

	return <PITLSummaryActionButton title="Schedule a Consult" onClick={onClick} />;
};

/**
 * Creates an ActionButton that will open the Results PDF.
 * The button will not be displayed if the patient is less than 18 years old (We don't allow minors to download reports).
 * SCR-8483: Minors can view Genova Connect results. 
 *
 * @param gearOrder The gear order
 */
export const ViewResultsAction = ({ gearOrder, accessionNo, button }) => {
	// Default to true, which will hide the button if we don't have patientInfo data
	let isMinor = true;
	const patientDOB = gearOrder?.patientInfo?.dateOfBirth;
	if (patientDOB) {
		const dob = new Date(gearOrder.patientInfo.dateOfBirth);
		isMinor = calculateAge(dob) < 18;
	}

	if (!patientDOB || (isMinor && !gearOrder?.physicianInTheLoop)) {
		return null;
	}

	// If an accessionNo is passed in, find the gearReq that contains that accession. Otherwise, find the first
	// gearReq that contains any accessionNo
	const gearReq = gearOrder.requisitions?.find(
		(gearReq) =>
			gearReq?.accessionNo &&
			(!accessionNo || !!gearReq.accessionDetails?.find((detail) => accessionNo === detail?.accessionNo))
	);
	const gearReqId = gearReq?.id;
	if (!accessionNo) {
		accessionNo = gearReq?.accessionNo;
	}
	if (!gearReqId || !accessionNo) {
		return null;
	}
	let pdfUrl = `${process.env.REACT_APP_GEAR_REST_URL}/gear-requisition/${gearReqId}/viewResultsPDF?dateOfBirth=${patientDOB}&accessionNo=${accessionNo}`;

	/**
	 * Opens a result PDF in a new window.
	 */
	const viewPDF = (event) => {
		event.preventDefault();
		// Call fetch() to generate a new access_token before opening the PDF window
		fetch()
			.then(() => {
				window.open(pdfUrl, '_blank');
			})
			.finally(() => {
				// Add a delay before logging the action to prevent a race condition where the log action nonce overwrites the PDF nonce
				setTimeout(() => {
					logEvent(
						'{} - "View Results" clicked. gearOrderId={}, accessionNo={}',
						'UI_ACTION_VIEW_RESULTS',
						gearOrder.id,
						accessionNo
					);
				}, 100);
			});
	};

	if (button) {
		return implementActionButton(button, { onClick: viewPDF });
	}

	return <PITLSummaryActionButton title="View Results" onClick={viewPDF} />;
};

/**
 * Opens a window to /pay with details that will pre-fill a payment request.
 */
export const PayBillAction = ({ gearOrderId, gearReqId, omsRequisitionId, accessionNo, dateOfBirth, button }) => {
	const hasRequiredData = !!(accessionNo && gearReqId && omsRequisitionId && dateOfBirth && button);
	if (!hasRequiredData) {
		return null;
	}

	const openPayPage = () => {
		window.open(
			`${process.env.REACT_APP_GDX_WEB_URL}/pay/ereq/${gearReqId}?requisitionNumber=${omsRequisitionId}&orderNo=${accessionNo}&dateOfBirth=${dateOfBirth}`,
			'_blank'
		);

		logEvent(
			'{} - "Pay Bill" clicked. gearOrderId={}, omsRequisitionId={}, accessionNo={}',
			'UI_ACTION_PAY_BILL',
			gearOrderId, omsRequisitionId, accessionNo
		);
	};

	return implementActionButton(button, { onClick: openPayPage });
};
